import { examDateRows } from "../../lib/examSchedules.js";
import { examGradeLabel, examSchoolColor } from "./examCalendar.js";

const sections = [
  { key: "middle", label: "중학교", levels: ["middle", "중등"] },
  { key: "high", label: "고등학교", levels: ["high", "고등"] },
  { key: "elementary", label: "초등학교", levels: ["elementary", "초등"] },
  { key: "unknown", label: "과정 미확인", levels: [] },
];
export const examSchoolKey = (period) =>
  period.schoolChoiceId
    ? `school:${period.schoolChoiceId}`
    : `legacy:${period.id}`;
export const examGradeKey = (period) =>
  period.grade === "" || period.grade == null
    ? "unspecified"
    : String(period.grade);
const sectionKey = (period) =>
  sections.find((section) =>
    section.levels.includes(period.schoolInfo?.schoolLevel)
  )?.key || "unknown";
const textOrder = (a, b) =>
  String(a || "").localeCompare(String(b || ""), "ko");
const gradeOrder = (a, b) =>
  (examGradeKey(a) === "unspecified" ? Infinity : Number(a.grade)) -
    (examGradeKey(b) === "unspecified" ? Infinity : Number(b.grade)) || 0;
const compare = (a, b) =>
  sections.findIndex((s) => s.key === sectionKey(a)) -
    sections.findIndex((s) => s.key === sectionKey(b)) ||
  textOrder(a.school, b.school) ||
  textOrder(a.schoolInfo?.city, b.schoolInfo?.city) ||
  textOrder(a.schoolInfo?.district, b.schoolInfo?.district) ||
  textOrder(examSchoolKey(a), examSchoolKey(b)) ||
  gradeOrder(a, b) || textOrder(a.id, b.id);

function valuesOnDate(period, date, kind) {
  const entries = kind === "subject"
    ? period.subjectEntries
    : period.directEntries;
  const field = kind === "subject" ? "name" : "note";
  const saved =
    entries?.filter((entry) => entry.date === date).map((entry) =>
      entry[field]
    ) || [];
  if (saved.length) {
    return saved.filter((value) => typeof value === "string" && value.trim());
  }
  const plan = period.subjects?.[date];
  const value = typeof plan === "string"
    ? (kind === "subject" ? plan : "")
    : plan?.[kind];
  return typeof value === "string" && value.trim() ? [value] : [];
}

// Display aggregation only: IDs, legacy links and source records are never rewritten.
export function examScheduleView(source, { school = "", grade = "" } = {}) {
  const sorted = source.toSorted(compare);
  const schoolMap = new Map();
  for (const period of sorted) {
    const key = examSchoolKey(period);
    if (!schoolMap.has(key)) {
      schoolMap.set(key, {
        value: key,
        label: period.school,
        identity: period.schoolChoiceId || period.id,
        legacy: !period.schoolChoiceId,
      });
    }
  }
  const schoolPeriods = sorted.filter((period) =>
    !school || examSchoolKey(period) === school
  );
  const gradeMap = new Map();
  for (const period of schoolPeriods) {
    gradeMap.set(examGradeKey(period), examGradeLabel(period.grade));
  }
  const gradeOptions = [...gradeMap].sort(([a], [b]) =>
    (a === "unspecified" ? Infinity : Number(a)) -
    (b === "unspecified" ? Infinity : Number(b))
  ).map(([value, label]) => ({ value, label }));
  const periods = schoolPeriods.filter((period) =>
    !grade || examGradeKey(period) === grade
  );
  const bounds = periods.flatMap((period) => [
    period.inputStart || period.start,
    period.inputEnd || period.end,
    ...(period.subjectEntries || []).filter((entry) => entry.name?.trim()).map((
      entry,
    ) => entry.date),
    ...(period.directEntries || []).filter((entry) => entry.note?.trim()).map((
      entry,
    ) => entry.date),
    ...Object.entries(period.subjects || {}).filter(([date]) =>
      valuesOnDate(period, date, "subject").length ||
      valuesOnDate(period, date, "direct").length
    ).map(([date]) => date),
  ]).filter(Boolean).sort();
  const dates = bounds.length ? examDateRows(bounds[0], bounds.at(-1)) : [];
  const rowsMap = new Map();
  for (const period of periods) {
    const key = JSON.stringify([examSchoolKey(period), examGradeKey(period)]);
    if (!rowsMap.has(key)) {
      rowsMap.set(key, {
        key,
        school: period.school,
        schoolKey: examSchoolKey(period),
        grade: period.grade,
        section: sectionKey(period),
        color: examSchoolColor(period),
        periods: [],
        cells: {},
      });
    }
    rowsMap.get(key).periods.push(period);
  }
  const rows = [...rowsMap.values()];
  for (const row of rows) {
    for (const date of dates) {
      const subjects = [
        ...new Set(
          row.periods.flatMap((p) => valuesOnDate(p, date, "subject")),
        ),
      ];
      const direct = [
        ...new Set(row.periods.flatMap((p) => valuesOnDate(p, date, "direct"))),
      ];
      row.cells[date] = {
        subjects,
        direct,
        inRange: !!(subjects.length || direct.length || row.periods.some((p) =>
          date >= (p.inputStart || p.start) && date <= (p.inputEnd || p.end)
        )),
      };
    }
  }
  return {
    periods,
    rows,
    dates,
    schoolOptions: [...schoolMap.values()],
    gradeOptions,
    sections: sections.map((section) => ({
      ...section,
      rows: rows.filter((row) => row.section === section.key),
    }))
      .filter((section) => section.rows.length),
  };
}
